import pkceChallenge from 'pkce-challenge';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { RefreshToken } from '@crossnokaye/typescript-sdk/models/components';

import { AppConfigs } from '@/config';
import { useAuthPopup } from '@/features/tokens/hooks/useAuthPopup';
import {
  fetchUserRefreshTokens,
  transformListUserRefreshTokensResponse,
} from '@/features/tokens/hooks/useListUserRefreshTokens';
import { useSetRefreshTokenAttributes } from '@/features/tokens/hooks/useSetRefreshTokenAttributes';
import {
  constructAuthorizationUrl,
  getRedirectUri,
} from '@/features/tokens/utils/constructAuthorizationUrl';
import { validateAuthResponse } from '@/features/tokens/utils/validateAuthResponse';
import { useAuthUser } from '@/providers/auth/auth-user-provider';

const accessTokenBaseUrl = AppConfigs.isLocal ? '/' : AppConfigs.atlasEnvUrl.toString();

export const useCreateRefreshToken = () => {
  const { user } = useAuthUser();
  const { openAuthPopup } = useAuthPopup();
  const queryClient = useQueryClient();
  const { mutate: updateTokenName } = useSetRefreshTokenAttributes();

  const fetchNewestToken = useCallback(async (): Promise<RefreshToken | undefined> => {
    const existingTokens = queryClient.getQueryData<RefreshToken[]>([
      'iam',
      'ListUserRefreshTokens',
    ]);

    const fetchTokens = await fetchUserRefreshTokens({ userId: user?.id ?? '' });
    const newTokenList = transformListUserRefreshTokensResponse(fetchTokens);

    return newTokenList.find((token) => !existingTokens?.some((t) => t.id === token.id));
  }, [user?.id, queryClient]);

  const initiateAuthRequest = useCallback(
    async (
      newTokenName: string,
      newTokenDescription?: string
    ): Promise<RefreshToken | undefined> => {
      const { code_verifier, code_challenge } = await pkceChallenge();
      const state = window.crypto.randomUUID();

      const authUrl = constructAuthorizationUrl({ code_challenge, state });

      const response = await openAuthPopup(authUrl);
      const code = validateAuthResponse(response, state);

      const loginResponse = await fetch(`${accessTokenBaseUrl}api/login/v3/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          code,
          code_verifier,
          redirect_uri: getRedirectUri(),
        }),
      });

      if (!loginResponse.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const tokens = await loginResponse.json();

      const newToken = await fetchNewestToken();

      if (newToken) {
        // call the setRefreshTokenAttributes function to set the name and description of the new token
        updateTokenName(
          {
            userId: user.id,
            tokenId: newToken.id,
            setRefreshTokenAttributesRequestBody: {
              name: newTokenName ?? '',
              description: newTokenDescription,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['iam', 'ListUserRefreshTokens'] });
            },
          }
        );
      }
      return tokens.refresh_token;
    },
    [openAuthPopup, fetchNewestToken, updateTokenName, user?.id, queryClient]
  );

  return {
    initiateAuthRequest,
  };
};
